import { EventDispatcher, GameTime, IParamHandle, inject, param, service } from "@spawn/core";
import { NudgingDirection, NudgingService, enableReel } from "@spawn/nudge";
import { NetworkService, ReelController } from "@spawn/slot-machine";
import { distinctUntilKeyChanged, map, pairwise } from "rxjs/operators";
import { GameEvents, StartConsecutiveNudgeUpsEvent } from "../events/events";
import { NrgLaglessGenerator } from "../lagless/customLaglessGenerator";
import { printReels, reelHasFlowers } from "../network/hacks";
import { IGameNetworkState } from "../network/networkState";
import { deepClone } from "../utils/object";
import { BaseSymbolTypes } from "./symbolService";

/**
 * Order of operations for nudgeups:
 * 1.a CreateNudgeStates if at the start of freegames with extra scatters (backend response is incomplete)
 * 1.b SetNudgeStates if we have won FG scatters during the freegames (backend response is complete)
 * 2. UpdateFullReelset
 * 3. PerformNudgeUp
 */
@service
export class NudgeUpService {
	@inject private readonly eventDispatcher!: EventDispatcher;
	@inject private readonly gameTime!: GameTime;
	@inject private readonly nudgingService!: NudgingService;
	@inject private readonly networkService!: NetworkService<IGameNetworkState>;
	@inject private readonly reelController!: ReelController;
	@inject private readonly laglessGenerator!: NrgLaglessGenerator;

	/** Duration of one nudge step of the nudge-ups, in milliseconds. */
	@param({ default: 500 }) nudgeUpDurationMs!: IParamHandle<number>;

	public initialNudgeups: number = 0;

	/**
	 * This tracks the number of new mysteries that have landed on each reel.
	 * (new = from one spin to the other, not as a result of nudging)
	 */
	public get reelsWithNewMysteries(): number[] {
		return this._reelsWithNewMysteries;
	}
	private _reelsWithNewMysteries: number[] = [0, 0, 0, 0, 0];

	constructor() {
		// eslint-disable-next-line @spawn/unmanaged-rxjs-subscription
		this.networkService.state$
			.pipe(
				// keep the previous and current reels between network state updates
				map((state) => state.slotGameBase),
				distinctUntilKeyChanged("reels"),
				pairwise(),
				map(([prev, current]) => [prev.reels, current.reels])
			)
			.subscribe(([prevReels, currentReels]) => {
				// check if any current reels have new mysteries (not part of this nudging series)
				for (const [index, currentReel] of currentReels.entries()) {
					if (
						currentReel.includes(BaseSymbolTypes.Mystery) && // current reel has 1+ mystery
						!prevReels[index].includes(BaseSymbolTypes.Mystery) // prev reel has no mysteries
					) {
						this._reelsWithNewMysteries[index] = currentReel.filter((symbol) => symbol === BaseSymbolTypes.Mystery).length;
					} else {
						this._reelsWithNewMysteries[index] = 0;
					}
				}
			});
	}

	/**
	 * This is used as the starting point for the nudging process at the beginning of the freegames.
	 * It takes the first FG spin response and:
	 * - adds nudgeCount mystery symbols to the beginning of mystery reels (2 and 4) to allow for the nudging animation to happen
	 * - replaces the non-mystery reels with random symbols so in the next spin we can show the actual replaced symbols
	 * It returns the intermediate reels (pre-nudge-up) and the final reels (post-nudge-up).
	 */
	public createNudgeInitialState(
		reels: string[][],
		nudgeCount: number
	): { intermediateReels: string[][]; finalReels: string[][] } {
		this.initialNudgeups = nudgeCount;

		// replace the non-mystery reels with random symbols so in the next spin we can show the actual replaced symbols
		// the seed is made up of the player id and the balance to make sure the same reels are generated for the same player+balance combination
		const seed = `${this.networkService.state.session.sessionId}-${this.networkService.state.balance}`;
		const finalReels = reels.map((reel, reelIndex) =>
			[0, 2, 4].includes(reelIndex) ? this.laglessGenerator.getDeterministicReelstrip(6, reelIndex, seed) : reel
		);

		const startingReels = finalReels.map((r, reelIndex) => {
			const firstNonMysteryIndex = r.lastIndexOf(BaseSymbolTypes.Mystery) + 1;
			const reel = deepClone(r);

			if ([1, 3].includes(reelIndex) && reelHasFlowers(reel)) {
				// add nudgeCount mystery symbols to prepare for the nudge-ups
				reel.splice(firstNonMysteryIndex, 0, ...new Array(nudgeCount).fill(BaseSymbolTypes.Mystery));
			}

			return reel;
		});

		printReels("dbg: true initial reels", reels);
		printReels("dbg: fake starting reels", startingReels);
		printReels("dbg: fake finalReels", finalReels);

		return {
			intermediateReels: startingReels, // pre-nudge-up reels
			finalReels, // post-nudge-up reels
		};
	}

	public async performNudgesUp(nudgeCount: number): Promise<void> {
		this.eventDispatcher.dispatch({
			type: GameEvents.StartConsecutiveNudgeUps,
			payload: { nudgeCount },
		} as StartConsecutiveNudgeUpsEvent);

		await this.gameTime.waitFor(0.5);

		for (let i = 0; i < nudgeCount; i++) {
			const nudges: Promise<unknown>[] = [];

			for (let j = 0; j < 5; j++) {
				// sliding window of the 6 symbols to be used for the nudge
				const reelface = this.networkService.state.preNudgeUpReels![j].slice(i, 6 + i);
				const offsets = this.nudgingService.getOffsets();

				if (offsets[j] !== 0) {
					nudges.push(
						this.nudgingService.nudge({
							reel: j,
							direction: NudgingDirection.Up,
							duration: this.nudgeUpDurationMs(),
							reelface,
						})
					);
				}
			}
			await Promise.all(nudges);
		}
	}

	public async performNudgesDown(): Promise<void> {
		const offsets = this.nudgingService.getOffsets();
		const nudges: Promise<unknown>[] = [];

		for (let reel = 0; reel < offsets.length; reel++) {
			if (offsets[reel] !== 0) {
				nudges.push(
					this.nudgingService.nudge({
						reel,
						direction: NudgingDirection.Down,
						duration: this.nudgeUpDurationMs(),
						reelface: this.networkService.state.slotGameBase.reels[reel].slice(0, 6),
					})
				);
			}
		}
		await Promise.all(nudges);
	}

	public enableNonNudgingReels(): void {
		const offsets = this.nudgingService.getOffsets();
		for (let reel = 0; reel < offsets.length; reel++) {
			if (offsets[reel] === 0) {
				enableReel(this.reelController, reel);
			}
		}
	}
}
